<template>
  <section id="hero" class="home3-hero">
    <div class="home3-hero__bg">
      <div class="home3-hero__gradient" />
      <div class="home3-hero__pattern" />
    </div>

    <v-container class="home3-hero__container">
      <v-row align="center" justify="center">
        <v-col cols="12" md="6" class="home3-hero__content">
          <div class="home3-hero__eyebrow">
            <v-icon small color="primary" class="mr-1">mdi-shield-check</v-icon>
            Safe &amp; Reliable School Transport
          </div>

          <h1 class="home3-hero__title">
            {{ $t('welcome_to_system', { systemName }) }}
          </h1>

          <p class="home3-hero__subtitle">
            Track school buses in real time, keep parents informed, and give administrators
            full visibility over every trip — all from one platform.
          </p>

          <div class="home3-hero__actions">
            <template v-if="!isUserLoggedIn">
              <v-btn
                large
                rounded
                color="primary"
                class="home3-hero__btn px-8"
                to="/register"
              >
                {{ $t('school_signup') }}
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
              <v-btn
                large
                rounded
                outlined
                dark
                class="home3-hero__btn px-8"
                to="/login"
              >
                {{ $t('Login') }}
              </v-btn>
            </template>
            <v-btn
              v-else
              large
              rounded
              color="primary"
              class="home3-hero__btn px-8"
              :to="dashboardRoute"
            >
              {{ $t('dashboard') }}
              <v-icon right>mdi-view-dashboard-outline</v-icon>
            </v-btn>

            <v-btn
              text
              dark
              class="home3-hero__btn-explore mt-2 mt-sm-0"
              @click="$vuetify.goTo('#features')"
            >
              Explore features
              <v-icon right small>mdi-chevron-down</v-icon>
            </v-btn>
          </div>

          <div class="home3-hero__stats d-flex flex-wrap mt-8">
            <div v-for="(stat, i) in stats" :key="i" class="home3-hero__stat mr-6 mb-3">
              <div class="home3-hero__stat-value">{{ stat.value }}</div>
              <div class="home3-hero__stat-label">{{ stat.label }}</div>
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="6" class="home3-hero__visual d-flex justify-center mt-8 mt-md-0">
          <div class="home3-hero__map-demo">
            <div class="home3-hero__map-grid" />
            <svg class="home3-hero__route" viewBox="0 0 420 240" preserveAspectRatio="none">
              <path
                d="M52 44 C120 34, 170 72, 224 112 C266 142, 312 166, 372 204"
                class="home3-hero__route-line"
              />
            </svg>

            <div class="home3-hero__point home3-hero__point--school">
              <v-icon x-small color="white">mdi-school</v-icon>
              <span>School</span>
            </div>
            <div class="home3-hero__point home3-hero__point--stop">
              <v-icon x-small color="white">mdi-map-marker</v-icon>
              <span>Student Stop</span>
            </div>

            <div class="home3-hero__bus-marker">
              <v-icon x-small color="white">mdi-bus-school</v-icon>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <div class="home3-hero__wave">
      <v-img src="@/assets/images/landing/borderWaves.svg" height="48" />
    </div>
  </section>
</template>

<script>
export default {
  props: {
    systemName: {
      type: String,
      required: true,
    },
    isUserLoggedIn: {
      type: Boolean,
      default: false,
    },
    userRole: {
      type: String,
      default: null,
    },
  },
  computed: {
    dashboardRoute() {
      return this.userRole === 'admin' ? '/admin-dashboard' : '/school-dashboard'
    },
    stats() {
      return [
        { value: '24/7', label: 'Live GPS' },
        { value: '100%', label: 'Trip visibility' },
        { value: '1', label: 'Unified platform' },
      ]
    },
  },
}
</script>

<style lang="scss" scoped>
@import './home3-theme.scss';

.home3-hero {
  position: relative;
  min-height: 92vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  padding-top: 5rem;

  &__bg {
    position: absolute;
    inset: 0;
    z-index: 0;
  }

  &__gradient {
    position: absolute;
    inset: 0;
    background: $home3-gradient;
  }

  &__pattern {
    position: absolute;
    inset: 0;
    opacity: 0.06;
    background-image: radial-gradient(circle at 20% 50%, #fff 1px, transparent 1px),
      radial-gradient(circle at 80% 20%, #fff 1px, transparent 1px);
    background-size: 48px 48px;
  }

  &__container {
    position: relative;
    z-index: 1;
    padding-top: 2rem;
    padding-bottom: 5rem;
  }

  &__eyebrow {
    display: inline-flex;
    align-items: center;
    padding: 6px 14px;
    margin-bottom: 1.25rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 100px;
    backdrop-filter: blur(8px);
  }

  &__title {
    font-size: clamp(2rem, 4.5vw, 3.25rem);
    font-weight: 700;
    line-height: 1.15;
    color: #fff;
    letter-spacing: -0.02em;
    margin-bottom: 1.25rem;
  }

  &__subtitle {
    font-size: 1.125rem;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.82);
    max-width: 520px;
    margin-bottom: 2rem;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
  }

  &__btn-explore {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 500;
  }

  &__stats {
    padding-top: 0.5rem;
    border-top: 1px solid rgba(255, 255, 255, 0.15);
  }

  &__stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: $home3-primary-light;
    line-height: 1.2;
  }

  &__stat-label {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.65);
    margin-top: 2px;
  }

  &__visual {
    justify-content: center;
  }

  &__map-demo {
    position: relative;
    width: 500px;
    height: 300px;
    border-radius: 18px;
    background: linear-gradient(145deg, rgba(96, 142, 196, 0.34), rgba(102, 119, 142, 0.32));
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: $home3-shadow-lg;
    overflow: hidden;
    backdrop-filter: blur(6px);
  }

  &__map-grid {
    position: absolute;
    inset: 0;
    opacity: 0.3;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.22) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.22) 1px, transparent 1px);
    background-size: 32px 32px;
  }

  &__route {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  &__route-line {
    fill: none;
    stroke: rgba(255, 255, 255, 0.82);
    stroke-width: 4;
    stroke-linecap: round;
    stroke-dasharray: 6 8;
  }

  &__point {
    position: absolute;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    font-size: 0.74rem;
    font-weight: 600;
    color: #fff;
    background: rgba(31, 65, 104, 0.95);
    border-radius: $home3-radius-sm;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    white-space: nowrap;

    &--school {
      top: 28px;
      left: 24px;
    }

    &--stop {
      right: 20px;
      bottom: 18px;
    }
  }

  &__bus-marker {
    position: absolute;
    left: 34px;
    top: 42px;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $home3-primary;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
    animation: home3-drive-roundtrip 8s ease-in-out infinite alternate;
  }

  &__wave {
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    z-index: 2;
    line-height: 0;
  }
}

@keyframes home3-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-12px); }
}

@keyframes home3-drive-roundtrip {
  0% {
    left: 44px;
    top: 50px;
    transform: rotate(10deg);
  }
  100% {
    left: 374px;
    top: 212px;
    transform: rotate(28deg);
  }
}

@media (max-width: 960px) {
  .home3-hero {
    min-height: auto;
    text-align: center;

    &__subtitle {
      margin-left: auto;
      margin-right: auto;
    }

    &__actions {
      justify-content: center;
    }

    &__stats {
      justify-content: center;
    }

    &__visual {
      margin-top: 2rem;
    }

    &__map-demo {
      width: 100%;
      max-width: 440px;
      height: 260px;
    }

    &__point {
      font-size: 0.68rem;
      padding: 6px 10px;
    }
  }
}

@media (max-width: 600px) {
  .home3-hero {
    &__map-demo {
      max-width: 330px;
      height: 210px;
    }

    &__point {
      font-size: 0.64rem;
      padding: 5px 7px;
    }

    &__point--school { top: 22px; left: 16px; }
    &__point--stop { right: 12px; bottom: 12px; }

    &__bus-marker {
      width: 24px;
      height: 24px;
    }
  }
}
</style>
